/**
 * siteLayerScope — pure helpers for the "Edit Map Layers" bulk tools
 * (mass delete by type + radius/boundary scoping). A KMZ upload can leave
 * a site with hundreds of layers swamping the map; these helpers partition
 * a `SiteLayer[]` into "keep" / "drop" sets so the editor can offer a
 * single staged bulk operation (revertible via the in-dialog undo stack).
 *
 * All functions are pure (no I/O, no React, no store) and operate on the
 * existing `SiteLayer` shape, so they drop straight into the editor's
 * draft-array model.
 *
 * Reuse, not reinvent (CLAUDE.md "modular design", "prefer first-principles
 * but reuse working maths"):
 *   - Great-circle distance: `haversineMeters` from `siteBoundaryGeometry`
 *     (WGS-84 mean radius, numerically-stable clamped asin).
 *   - Vertex-mean centroid: `polygonCentroid` from `siteBoundaryGeometry`
 *     (same convention the boundary editor + report covers use).
 *   - Point-in-polygon: ray-casting `pointInPolygon` from
 *     `geometry/sampleGrid` (closed-ring crossing-number test).
 *   - Finite-vertex guard: `isFiniteLatLng` from `siteBoundaryGeometry`.
 *
 * Units: lat/lng in WGS-84 DEGREES; radius input in km or mi (converted to
 * metres at the boundary via named constants — no magic numbers).
 */
import type { SiteLayer } from '../types/site';
import {
  haversineMeters,
  isFiniteLatLng,
} from './siteBoundaryGeometry';
import { pointInPolygon } from './geometry/sampleGrid';

export type LatLng = { lat: number; lng: number };

/** Metres per kilometre. */
const M_PER_KM = 1_000;
/** Metres per statute mile (international mile, exact). */
const M_PER_MILE = 1_609.344;

export type RadiusUnit = 'km' | 'mi';

/** Convert a radius expressed in `unit` to metres. */
export function radiusToMeters(value: number, unit: RadiusUnit): number {
  return value * (unit === 'mi' ? M_PER_MILE : M_PER_KM);
}

/**
 * The single lat/lng used to test whether a layer falls inside a radius or
 * boundary. Representative-coordinate convention (flagged for review — this
 * is the main modelling choice):
 *
 *   - POINT   → the point itself (its first/only vertex).
 *   - LINE    → the arithmetic mean of its vertices (its centroid). A line
 *               that merely passes near the site can therefore be dropped if
 *               its *midpoint* is outside the scope even though one end is
 *               inside; this matches the "keep only layers belonging to this
 *               site" intent better than an any-vertex test, which would keep
 *               a 50 km transmission line that just clips the radius.
 *   - POLYGON → its vertex-mean centroid (same convention as the boundary
 *               aggregate + report covers).
 *
 * Returns `null` when the layer has no finite vertex (malformed/legacy KMZ),
 * so callers can decide how to treat un-locatable layers (we KEEP them — a
 * destructive scope op should never silently drop a layer we couldn't place).
 */
export function layerRepresentativeCoordinate(layer: SiteLayer): LatLng | null {
  const coords = layer?.geometry?.coordinates;
  if (!Array.isArray(coords) || coords.length === 0) return null;
  const finite = coords.filter(isFiniteLatLng);
  if (finite.length === 0) return null;
  if (layer.geometry.type === 'point') {
    return finite[0]!;
  }
  // line / polygon → arithmetic vertex-mean. This is correct for BOTH a line
  // (no area) and a polygon; `polygonCentroid` is AREA-weighted and degenerates
  // to NaN on a zero-area line (e.g. a 2-vertex line straddling a boundary
  // edge), so we mean the vertices directly here.
  let latSum = 0;
  let lngSum = 0;
  for (const v of finite) {
    latSum += v.lat;
    lngSum += v.lng;
  }
  const c: LatLng = { lat: latSum / finite.length, lng: lngSum / finite.length };
  return isFiniteLatLng(c) ? c : finite[0]!;
}

/**
 * Result of a partition: `keep` are the layers that stay, `drop` are the
 * layers that would be removed, and `unlocatable` is the subset of `keep`
 * that had no finite coordinate (kept defensively — surfaced so the UI can
 * footnote "N layers kept (no coordinate)").
 */
export interface LayerPartition {
  keep: SiteLayer[];
  drop: SiteLayer[];
  unlocatable: SiteLayer[];
}

/**
 * Keep only layers whose representative coordinate is within `radiusMeters`
 * great-circle of `center`; drop the rest. Layers with no finite coordinate
 * are KEPT (never silently dropped) and reported in `unlocatable`.
 *
 * A non-finite/≤0 radius keeps everything (no-op) — guards a fat-fingered
 * empty input from nuking every layer.
 */
export function partitionLayersByRadius(
  layers: readonly SiteLayer[],
  center: LatLng,
  radiusMeters: number,
): LayerPartition {
  const keep: SiteLayer[] = [];
  const drop: SiteLayer[] = [];
  const unlocatable: SiteLayer[] = [];
  const validRadius = Number.isFinite(radiusMeters) && radiusMeters > 0;
  const validCenter = isFiniteLatLng(center);
  for (const layer of layers) {
    const coord = layerRepresentativeCoordinate(layer);
    if (!coord || !validCenter || !validRadius) {
      keep.push(layer);
      if (!coord) unlocatable.push(layer);
      continue;
    }
    if (haversineMeters(center, coord) <= radiusMeters) {
      keep.push(layer);
    } else {
      drop.push(layer);
    }
  }
  return { keep, drop, unlocatable };
}

/**
 * Keep only layers whose representative coordinate falls inside the boundary
 * ring (closed polygon); drop the rest. A ring with < 3 vertices keeps
 * everything (no usable boundary). Un-locatable layers are KEPT + reported.
 */
export function partitionLayersByBoundary(
  layers: readonly SiteLayer[],
  boundary: readonly LatLng[],
): LayerPartition {
  return partitionLayersByBoundaries(layers, [boundary]);
}

/**
 * Keep only layers whose representative coordinate falls inside at least one
 * usable parcel boundary. This is point-membership in the geometric union of
 * the supplied rings: disjoint and overlapping parcels both use the same
 * "inside any ring" rule. Rings with fewer than three finite vertices are
 * ignored; when no usable ring remains, the operation is a defensive no-op.
 * Un-locatable layers are KEPT + reported.
 */
export function partitionLayersByBoundaries(
  layers: readonly SiteLayer[],
  boundaries: readonly (readonly LatLng[])[],
): LayerPartition {
  const keep: SiteLayer[] = [];
  const drop: SiteLayer[] = [];
  const unlocatable: SiteLayer[] = [];
  const rings = Array.isArray(boundaries)
    ? boundaries
      .map(boundary => Array.isArray(boundary) ? boundary.filter(isFiniteLatLng) : [])
      .filter(ring => ring.length >= 3)
    : [];
  const usable = rings.length > 0;
  for (const layer of layers) {
    const coord = layerRepresentativeCoordinate(layer);
    if (!coord || !usable) {
      keep.push(layer);
      if (!coord) unlocatable.push(layer);
      continue;
    }
    if (rings.some(ring => pointInPolygon(coord, ring))) {
      keep.push(layer);
    } else {
      drop.push(layer);
    }
  }
  return { keep, drop, unlocatable };
}

/**
 * Partition by geometry type — `match` keeps the layers of the given type,
 * `rest` is everything else. Used by the "delete all POINT/LINE/AREA"
 * controls (delete = keep `rest`).
 */
export function partitionLayersByType(
  layers: readonly SiteLayer[],
  type: SiteLayer['geometry']['type'],
): { match: SiteLayer[]; rest: SiteLayer[] } {
  const match: SiteLayer[] = [];
  const rest: SiteLayer[] = [];
  for (const layer of layers) {
    if (layer?.geometry?.type === type) match.push(layer);
    else rest.push(layer);
  }
  return { match, rest };
}

/** Count of each geometry type in a layer list — drives the toolbar labels. */
export function countLayersByType(layers: readonly SiteLayer[]): {
  point: number;
  line: number;
  polygon: number;
} {
  const counts = { point: 0, line: 0, polygon: 0 };
  for (const layer of layers) {
    const t = layer?.geometry?.type;
    if (t === 'point') counts.point += 1;
    else if (t === 'line') counts.line += 1;
    else if (t === 'polygon') counts.polygon += 1;
  }
  return counts;
}
